import React, { useState } from "react";
import { cn } from "../../../utils/cn";
import { useTheme } from "../../../context/ThemeContext";
import searchIconImg from "../../../assets/icons/search-icon.png";

const Input = ({
  id,
  name,
  label,
  placeholder,
  type = "text",
  value,
  onChange,
  onBlur,
  touched = {},
  isRequired = false,
  labelStyle = "",
  inputStyle = "",
  error,
  className = "",
  containerStyle = "",
  searchIcon = false,
  icon,
  iconPosition = "right",
  allowFillColor = false,
  disabled = false,
  isFilterSearch = false,
  inlineCheckbox = false,
  helperText,
  maxLength,
  showCharCount = false,
  ...props
}) => {
  const { theme } = useTheme();
  const [isFocused, setIsFocused] = useState(false);

  // const showError = error && touched[name]; // Only show if touched
  const showError = !!error;

  const getBorderClasses = () => {
    if (showError) return "border-[var(--color-error)]";
    if (isFocused) return "border-[var(--color-stroke-01)]";
    return "border-[var(--color-stroke-07)]";
  };

  // Calculate character count
  const currentLength = value?.length || 0;
  const isNearLimit = maxLength && currentLength >= maxLength * 0.9 && currentLength < maxLength;
  const isAtLimit = maxLength && currentLength >= maxLength;

  // Get helper text color based on state
  const getHelperTextColor = () => {
    if (showError) return "text-[var(--color-error)]";
    if (isAtLimit) return "text-red-500";
    if (isNearLimit) return "text-orange-500";
    return "text-[var(--color-content-02)]";
  };

  // Textarea
  if (type === "textarea") {
    return (
      <div className={cn("flex flex-col w-full", containerStyle)}>
        {label && (
          <label
            htmlFor={id}
            className={cn(
              "mb-2 text-sm font-medium text-[var(--color-label)] font-inter",
              labelStyle,
            )}
          >
            {label}
            {isRequired && (
              <span className="ml-1 text-[var(--color-error)]">*</span>
            )}
          </label>
        )}
        <textarea
          id={id}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          maxLength={maxLength}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            setIsFocused(false);
            if (onBlur) onBlur(e);
          }}
          className={cn(
            `w-full min-h-[100px] px-4 py-3 rounded-lg border !border-[var(--color-stroke-12)] font-inter text-sm 
             placeholder:text-[var(--color-content-02)] placeholder:opacity-60 
             focus:outline-none ${
               allowFillColor && value ? "bg-[var(--color-background-03)]" : ""
             } 
             ${getBorderClasses()}`,
            className,
            inputStyle,
          )}
          {...props}
        />
        
        {/* Helper Text Section */}
        <div className="mt-1 min-h-[20px] flex justify-between items-start">
          <div className="flex-1">
            {!isFilterSearch && showError && (
              <span
                className="text-[12px] leading-[16px] font-inter text-[var(--color-error)] transition-opacity duration-200"
                style={{ display: "block", wordBreak: "break-word" }}
              >
                {error}
              </span>
            )}
            {!showError && helperText && (
              <span
                className={cn(
                  "text-[12px] leading-[16px] font-inter transition-opacity duration-200",
                  getHelperTextColor()
                )}
                style={{ display: "block", wordBreak: "break-word" }}
              >
                {helperText}
              </span>
            )}
          </div>
          
          {/* Character Counter */}
          {maxLength && showCharCount && (
            <span
              className={cn(
                "text-[11px] font-inter ml-2 whitespace-nowrap",
                isAtLimit ? "text-red-500 font-bold" : isNearLimit ? "text-orange-500" : "text-[var(--color-content-02)]"
              )}
            >
              {currentLength}/{maxLength}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Default Input
  return (
    <div className={cn("flex flex-col w-full", containerStyle)}>
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "mb-2 text-sm font-medium text-[var(--color-label)] font-inter",
            labelStyle,
          )}
        >
          {label}
          {isRequired && (
            <span className="ml-1 text-[var(--color-error)]">*</span>
          )}
        </label>
      )}
      <div className="relative flex items-center">
        {searchIcon && (
          <img
            src={searchIconImg}
            alt="search"
            className="absolute w-5 h-5 transform -translate-y-1/2 top-1/2 left-3"
          />
        )}
        <input
          type={type}
          id={id}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          maxLength={maxLength}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            setIsFocused(false);
            if (onBlur) onBlur(e);
          }}
          className={cn(
            `w-full h-12 px-4 rounded-lg border !border-[var(--color-stroke-12)] font-inter text-sm 
             placeholder:opacity-60 
             focus:outline-none ${
               allowFillColor && value ? "bg-[var(--color-background-03)]" : ""
             } 
             ${getBorderClasses()} ${searchIcon ? "pl-10" : ""} ${
               icon && iconPosition === "left" ? "pl-10" : 
               icon && iconPosition === "right" ? "pr-10" : ""
             }`,
            className,
            inputStyle,
          )}
          {...props}
        />
        {icon && (
          <span
            className={cn(
              "absolute top-1/2 -translate-y-1/2 text-[var(--color-content)]",
              iconPosition === "left" ? "left-3" : "right-3",
            )}
          >
            {icon}
          </span>
        )}
      </div>

      {/* Helper Text Section */}
      <div className="mt-1 min-h-[20px] flex justify-between items-start">
        <div className="flex-1">
          {!isFilterSearch && showError && (
            <span
              className="text-[12px] leading-[16px] font-inter text-[var(--color-error)]"
              style={{ display: "block", wordBreak: "break-word" }}
            >
              {error}
            </span>
          )}
          {!showError && helperText && (
            <span
              className={cn(
                "text-[12px] leading-[16px] font-inter transition-opacity duration-200",
                getHelperTextColor()
              )}
              style={{ display: "block", wordBreak: "break-word" }}
            >
              {helperText}
            </span>
          )}
        </div>
        
        {/* Character Counter */}
        {maxLength && showCharCount && (
          <span
            className={cn(
              "text-[11px] font-inter ml-2 whitespace-nowrap",
              isAtLimit ? "text-red-500 font-bold" : isNearLimit ? "text-orange-500" : "text-[var(--color-content-02)]"
            )}
          >
            {currentLength}/{maxLength}
          </span>
        )}
      </div>
    </div>
  );
};

export default Input;
